import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons/faArrowRotateLeft'
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye'
import { evaluationsService } from 'api/courses/evaluations'
import { createInitialEvaluationResume } from 'helpers/evaluations/initialEvaluationResume'
import { ModalButtons } from 'components/reusables/modalButtons'
import { Modal } from 'components/reusables/modal'
import { useEvaluation } from '../../contexts/Evaluation'
import Detail from './detail'

interface IProps {
  evaluationId: number
  setViewResults: (value: boolean) => void
}

export const Results = ({ evaluationId, setViewResults }: IProps) => {
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [evaluation, setEvaluation] = useState(createInitialEvaluationResume)
  const [error, setError] = useState<string | null>(null)
  const {
    setOnEvaluation,
    refreshSections,
    refreshEvaluation,
    onEvaluationUpdated,
  } = useEvaluation()
  const translationPrefix = 'lessons.contentBlocks.evaluation'

  useEffect(() => {
    setIsLoading(true)
    evaluationsService
      .getEvaluation(evaluationId)
      .then((response) => {
        setEvaluation(response)
      })
      .catch(() => {
        setIsLoading(false)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [evaluationId])

  const primaryAction = async () => {
    setIsProcessing(true)
    setError(null)

    try {
      if (evaluation.attemptsRemaining === 0 || evaluation.approved) {
        setOnEvaluation?.(false)
        onEvaluationUpdated(evaluation)
      } else {
        await evaluationsService.resetEvaluationResponse(
          evaluation.evaluationResponse?.id || 0,
          false,
          true
        )

        // Actualizar la evaluación para obtener el nuevo evaluation_response
        await refreshEvaluation()

        // También actualizar la evaluación local del modal
        const updatedEvaluation = await evaluationsService.getEvaluation(
          evaluation.id
        )
        setEvaluation(updatedEvaluation)

        // Cerrar el modal de resultados primero
        setViewResults(false)

        // Cargar secciones en modo normal (no review) después de un breve delay
        // para asegurar que el contexto se haya actualizado
        setTimeout(() => {
          refreshSections(false)
        }, 50)
      }
    } catch (error: any) {
      console.error('Error al reintentar evaluación:', error)
      setError(
        error?.response?.data?.error || 'Error al reintentar la evaluación'
      )

      // Actualizar la evaluación para obtener el estado actual (intentos restantes)
      try {
        const updatedEvaluation = await evaluationsService.getEvaluation(
          evaluation.id
        )
        setEvaluation(updatedEvaluation)
      } catch (refreshError) {
        console.error('Error al actualizar evaluación:', refreshError)
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const reviewAnswers = async () => {
    setIsProcessing(true)

    try {
      refreshSections(true)
      setViewResults(false)
    } catch (_error) {
      // Do nothing
    } finally {
      setIsProcessing(false)
    }
  }

  const primaryText =
    evaluation.attemptsRemaining === 0 || evaluation.approved
      ? t(`${translationPrefix}.buttons.continueWithTheLesson`)
      : t(`${translationPrefix}.buttons.retry`)

  const iconPrimary =
    evaluation.attemptsRemaining === 0 || evaluation.approved
      ? undefined
      : faArrowRotateLeft

  const actions = (
    <ModalButtons
      primaryText={primaryText}
      primaryAction={primaryAction}
      disableSecondaryButton={!evaluation.showResults}
      secondaryText={t(`${translationPrefix}.buttons.reviewAnswers`)}
      secondaryAction={reviewAnswers}
      processing={isProcessing}
      iconPrimary={iconPrimary}
      iconSecondary={faEye}
    />
  )

  const title = evaluation.approved
    ? t(`${translationPrefix}.evaluationApproved`)
    : t(`${translationPrefix}.evaluationNotApproved`)

  /**
   * Cerrar con la X debe disparar la misma actualización que "Continuar con la lección":
   * GET lección → `lesson` en Redux cambia → el `reload` de ContentBlock vuelve a pedir
   * `LESSONS_GET_LESSON_CONTENT` y `currentLessonContent` refleja el intento cerrado.
   * Sin esto, al volver a la lista el `reload` no se re-ejecuta (mismas deps) y el botón
   * Siguiente/Finalizar sigue leyendo bloques viejos hasta recargar la página.
   */
  const handleClose = async () => {
    try {
      const latest = await evaluationsService.getEvaluation(evaluationId)
      onEvaluationUpdated(latest)
    } catch {
      if (evaluation.id) {
        onEvaluationUpdated(evaluation)
      }
    } finally {
      setOnEvaluation?.(false)
    }
  }

  return (
    <Modal
      title={title}
      close={handleClose}
      isApproved={evaluation.approved}
      maxwidth={820}
      maxheight={600}
      actions={actions}
    >
      <Detail evaluation={evaluation} isLoading={isLoading} />
    </Modal>
  )
}

export default Results
